// ============================================================
// Flourish.AI — Permission System
// ============================================================

export type PermissionAction =
  | 'view' |'create' |'edit' |'delete' |'approve' |'export' |'import' |'manage_settings';

export type ModuleKey =
  | 'start_here' |'control_tower' |'profit_allocation' |'scenario_forecast' |'ai_scenario_advisor' |'risk_dashboard' |'pool_growth' |'pool_investment' |'pool_reserve' |'pool_innovation' |'pool_rewards' |'pool_banking' |'bank_registry' |'investment_portfolio' |'team_reserves' |'admin_config' |'planning_tracking' |'user_management';

export interface ModuleDefinition {
  key: ModuleKey;
  name: string;
  group: string;
  href: string;
}

export const MODULES: ModuleDefinition[] = [
  { key: 'start_here', name: 'Start Here', group: 'Core', href: '/start' },
  { key: 'control_tower', name: 'Control Tower', group: 'Core', href: '/' },
  { key: 'profit_allocation', name: 'Profit Allocation', group: 'Core', href: '/profit-allocation-strategic-pools' },
  { key: 'scenario_forecast', name: 'Scenario & Forecast', group: 'Analysis', href: '/scenario-forecast-analysis' },
  { key: 'ai_scenario_advisor', name: 'AI Scenario Advisor', group: 'Analysis', href: '/ai-scenario-advisor' },
  { key: 'risk_dashboard', name: 'Risk Dashboard', group: 'Analysis', href: '/risk-management-dashboard' },
  { key: 'pool_growth', name: 'Pool 1 — Growth', group: 'Pools', href: '/pools/growth-development' },
  { key: 'pool_investment', name: 'Pool 2 — Investment', group: 'Pools', href: '/pools/investment-wealth' },
  { key: 'pool_reserve', name: 'Pool 3 — Reserve', group: 'Pools', href: '/pools/reserve-protection' },
  { key: 'pool_innovation', name: 'Pool 4 — Innovation', group: 'Pools', href: '/pools/innovation-opportunity' },
  { key: 'pool_rewards', name: 'Pool 5 — Rewards', group: 'Pools', href: '/pools/team-reward-recognition' },
  { key: 'pool_banking', name: 'Pool Banking', group: 'Management', href: '/pool-banking' },
  { key: 'bank_registry', name: 'Bank Registry', group: 'Management', href: '/pool-banking/bank-registry' },
  { key: 'investment_portfolio', name: 'Investment Portfolio', group: 'Management', href: '/portfolio-investment-management' },
  { key: 'team_reserves', name: 'Team & Reserves', group: 'Management', href: '/team-compensation-reserves' },
  { key: 'admin_config', name: 'Admin Config', group: 'System', href: '/admin-configuration' },
  { key: 'planning_tracking', name: 'Planning & Tracking', group: 'System', href: '/planning-tracking' },
  { key: 'user_management', name: 'User Management', group: 'System', href: '/admin/user-management' },
];

export const MODULE_GROUPS = ['Core', 'Analysis', 'Pools', 'Management', 'System'];

export const PERMISSION_ACTIONS: { key: PermissionAction; label: string }[] = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
  { key: 'approve', label: 'Approve' },
  { key: 'export', label: 'Export' },
  { key: 'import', label: 'Import' },
  { key: 'manage_settings', label: 'Settings' },
];

export const DEPARTMENTS = [
  'Finance',
  'Operations',
  'Investment',
  'Risk',
  'Executive Management',
  'System Admin',
];

export const DATA_ACCESS_LEVELS = [
  { key: 'own', label: 'View Own Data Only' },
  { key: 'department', label: 'View Department Data' },
  { key: 'all', label: 'View All Company Data' },
];

// Permission set: moduleKey -> Set<action>
export type PermissionSet = Record<string, Set<PermissionAction>>;

// Build permission set from flat list
export function buildPermissionSet(
  perms: Array<{ module_key: string; action: PermissionAction; granted: boolean }>
): PermissionSet {
  const set: PermissionSet = {};
  for (const p of perms) {
    if (!p.granted) continue;
    if (!set[p.module_key]) set[p.module_key] = new Set();
    set[p.module_key].add(p.action);
  }
  return set;
}

// Permission helper factory — returns helpers bound to a permission set
export function createPermissionHelpers(permSet: PermissionSet) {
  const check = (module: string, action: PermissionAction): boolean => {
    return permSet[module]?.has(action) ?? false;
  };
  return {
    canView: (module: string) => check(module, 'view'),
    canCreate: (module: string) => check(module, 'create'),
    canEdit: (module: string) => check(module, 'edit'),
    canDelete: (module: string) => check(module, 'delete'),
    canApprove: (module: string) => check(module, 'approve'),
    canExport: (module: string) => check(module, 'export'),
    canImport: (module: string) => check(module, 'import'),
    canManageSettings: (module: string) => check(module, 'manage_settings'),
    hasAnyAccess: (module: string) => (permSet[module]?.size ?? 0) > 0,
  };
}

// Map route href to module key
export function hrefToModuleKey(href: string): ModuleKey | null {
  const mod = MODULES.find((m) => m.href === href || href.startsWith(m.href + '/'));
  return mod?.key ?? null;
}

// Default role-based permission set (fallback when DB not loaded)
export const DEFAULT_ROLE_PERMISSIONS: Record<string, PermissionSet> = {
  admin: buildPermissionSet(
    MODULES.flatMap((m) =>
      PERMISSION_ACTIONS.map((a) => ({ module_key: m.key, action: a.key, granted: true }))
    )
  ),
  manager: buildPermissionSet(
    MODULES.filter((m) => m.group !== 'System' || m.key === 'planning_tracking').flatMap((m) =>
      PERMISSION_ACTIONS.filter((a) => a.key !== 'manage_settings').map((a) => ({
        module_key: m.key,
        action: a.key,
        granted: true,
      }))
    )
  ),
  viewer: buildPermissionSet(
    MODULES.filter((m) => ['Core', 'Analysis', 'Pools'].includes(m.group)).map((m) => ({
      module_key: m.key,
      action: 'view' as PermissionAction,
      granted: true,
    }))
  ),
};
